import { MonthlyReportHelper } from "@deathstar/types/blazar";
import {
    Coverage,
    MonthlyReport,
    MonthlyReportData,
    MonthlyReportQueryResult,
    MonthlyReportStatus,
    Policy,
} from "@deathstar/types/northstar";
import { DateTime } from "@deathstar/types/util";
import { PaymentMethodInfo } from "@deathstar/types/waypoint";
import { Alert, classNames, PrimaryButton, printElement, useSnackbar } from "@deathstar/ui";
import {
    CheckCircleIcon,
    ChevronLeftIcon,
    CurrencyDollarIcon,
    ExclamationTriangleIcon,
    MapIcon,
    PrinterIcon,
    XMarkIcon,
} from "@heroicons/react/24/outline";
import { CircularProgress, Dialog, TextField, Tooltip } from "@material-ui/core";
import { Autocomplete, Skeleton } from "@material-ui/lab";
import { debounce } from "lodash";
import moment from "moment";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../../../api/api";
import { useAccountId } from "../../../api/useAccountId";
import { NotFoundError } from "../../../api/util/exceptions";
import Loader from "../../../components/loader/Loader";
import { queryClient } from "../../../util/queryClient";
import NewPaymentMethodForm from "../../billing/NewPaymentMethodForm";
import { PendingPolicyChangesFooter, usePendingPolicyChanges } from "../../pending-policy-changes/PendingPolicyChanges";
import { MonthlyReportCalculations } from "./MonthlyReportCalculations";
import { MonthlyReportStatic } from "./MonthlyReportStatic";
import { MonthlyReportUnitsTable } from "./MonthlyReportUnitsTable";
import { useBillingDetails } from "./useBillingDetails";

const COUNTDOWN = 5;

interface Step {
    id: string;
    label: string;
}

export default function MonthlyReportView() {
    const navigate = useNavigate();

    const accountId = useAccountId();
    const { policyId, year: yearStr, month: monthStr, step } = useParams();
    const month = monthStr ? parseInt(monthStr, 10) : 0;
    const year = yearStr ? parseInt(yearStr, 10) : 0;

    const m = moment()
        .month(month - 1)
        .year(year);

    const { data: report, isLoading } = api.monthlyReports.useFindForMonth(accountId!, policyId!, year, month, {
        enabled: !!policyId && !!year && !!month,
        staleTime: 60_000,
        retry: (count, e) => (e instanceof NotFoundError ? false : count < 3),
    });
    const { refetch: refetchPolicies } = api.policies.useFind(accountId!, "active");

    const data = useMemo<MonthlyReportData | null>(() => {
        if (report && report.layers.length > 0) {
            return MonthlyReportHelper.getMonthlyReportData(report.report, report.layers, report.equipment);
        }
        return null;
    }, [report]);

    const ref = useRef<HTMLDivElement>(null);

    const [countdown, setCountdown] = useState(COUNTDOWN);
    const billingDetails = useBillingDetails({ policyId: policyId!, year, month });
    const { changes: pendingPolicyChanges } = usePendingPolicyChanges();
    const hasPendingPolicyChanges = pendingPolicyChanges.some((change) => change.accountId === accountId);

    const steps = useMemo<Step[]>(() => {
        if (!data) {
            return [];
        }
        const steps: Step[] = [];
        if (data.isUnitReporter) {
            const label =
                data.equipment.activeTractors.length || data.equipment.activeTrailers.length
                    ? "Equipment"
                    : data.equipment.activeDrivers.length
                    ? "Drivers"
                    : "Equipment";
            steps.push({ id: "equipment", label });
        }
        if (data.isMileageReporter || data.isRevenueReporter || data.isPayrollReporter) {
            steps.push({
                id: "input",
                label: [data.isMileageReporter && "Mileage", data.isRevenueReporter && "Revenue", data.isPayrollReporter && "Payroll"]
                    .filter(Boolean)
                    .join(" & "),
            });
        }
        steps.push({ id: "premium", label: "Review" });
        steps.push({ id: "submit", label: "Submit" });
        return steps;
    }, [data]);

    const arrearsReminder = useMemo(() => {
        if (!report || !data) return null;

        const today = moment();
        const dayOfMonth = today.date();
        if (dayOfMonth < 15) return null;

        const isCompositeArrears = Policy.isCompositeArrears(data.endLayer.policy);

        if (!isCompositeArrears) return null;

        const reportStatus = MonthlyReportHelper.getReportStatus(
            year,
            month,
            report.report.isFinalized,
            report.report.isSubmitted,
            false,
            data.endLayer.policy.cancelEffectiveDate || undefined
        );

        return {
            show: reportStatus === MonthlyReportStatus.Due || reportStatus === MonthlyReportStatus.Overdue,
            dueMonth: DateTime.fromDate(moment().month(month).year(year).toDate()),
        };
    }, [report, data, year, month]);

    useEffect(() => {
        if (!!year && !!month && steps.length) {
            if (step) {
                if (!steps.some((s) => s.id === step)) {
                    navigate(`./../${steps[0].id}`, { replace: true });
                }
            } else {
                navigate(`./${steps[0].id}`, { replace: true });
            }
        }
    }, [step, navigate, year, month, steps]);

    useEffect(() => {
        if (step === "submit") {
            const int = setInterval(() => {
                setCountdown((c) => {
                    if (c === 1) {
                        clearInterval(int);
                    }
                    return c - 1;
                });
            }, 1000);
            return () => {
                clearTimeout(int);
            };
        }
        return undefined;
    }, [step]);

    const goNext = useCallback(() => {
        if (!data) {
            return;
        }
        navigate(`./../${steps[steps.findIndex((s) => s.id === step) + 1].id}`);
    }, [data, navigate, step, steps]);

    const goBack = useCallback(() => {
        if (!data) {
            return;
        }
        setCountdown(COUNTDOWN);
        navigate(`./../${steps[steps.findIndex((s) => s.id === step) - 1].id}`);
    }, [data, navigate, step, steps]);

    const submit = api.monthlyReports.useSubmit(accountId!, {
        onSuccess: async () => {
            await refetchPolicies();
            api.policies.cacheBust(policyId!);
            // if (data.endLayer.policy.billMethod === Policy.BillMethod.AGENCY && billingProfile && !billingProfile.automaticBillingEnabled) {
            //     setPaying(true);
            // }
        },
    });

    if (isLoading) {
        return <Loader />;
    }

    if (!report) {
        return (
            <div className="flex w-full flex-col">
                <div className="flex items-center justify-between gap-4 bg-waypoint-blue p-4 pb-2 text-white">
                    <div className="flex shrink items-center gap-2 overflow-hidden">
                        <button
                            className="rounded-full p-2 hover:bg-black/10"
                            onClick={() => {
                                if (steps.findIndex((s) => s.id === step) <= 0) {
                                    navigate("../monthly-reports/" + policyId);
                                } else {
                                    goBack();
                                }
                            }}
                        >
                            <ChevronLeftIcon className="h-5 w-5" />
                        </button>
                        <p className="overflow-hidden text-ellipsis whitespace-nowrap text-lg font-medium">
                            {m.format("MMMM YYYY")} report
                        </p>
                    </div>
                    <button
                        type="button"
                        aria-label="Close monthly report"
                        className="shrink-0 rounded-full p-2 hover:bg-black/10"
                        onClick={() => navigate("./../../..")}
                    >
                        <XMarkIcon className="h-5 w-5" />
                    </button>
                </div>
                <div className="flex w-full grow flex-col items-center justify-center text-xl">We could not find this report.</div>
            </div>
        );
    }

    if (!data) {
        return <Loader />;
    }

    return (
        <div className="flex w-full flex-col">
            <div className="hidden">
                <MonthlyReportStatic report={report.report} data={data} ref={ref} />
            </div>
            <div className="flex w-full shrink-0 items-center justify-between gap-2 overflow-hidden bg-waypoint-blue p-4 pb-2 text-white">
                <div className="flex shrink items-center gap-2 overflow-hidden">
                    <button
                        className="rounded-full p-2 hover:bg-black/10"
                        onClick={() => {
                            if (steps.findIndex((s) => s.id === step) <= 0) {
                                navigate("./../../..");
                            } else {
                                goBack();
                            }
                        }}
                    >
                        <ChevronLeftIcon className="h-5 w-5" />
                    </button>
                    <p className="overflow-hidden text-ellipsis whitespace-nowrap text-lg font-medium">{m.format("MMMM YYYY")} report</p>
                </div>
                <div className="flex shrink-0 grow items-center justify-center gap-6 text-sm">
                    {steps.map((s, i) => (
                        <div key={s.id} className={classNames("flex items-center gap-1", step !== s.id && "hidden text-white/80 md:flex")}>
                            <div
                                className={classNames(
                                    "flex h-5 w-5 items-center justify-center rounded-full transition-all",
                                    step === s.id ? "bg-white text-waypoint-blue" : "bg-waypoint-blue"
                                )}
                            >
                                {i + 1}
                            </div>
                            {s.label}
                        </div>
                    ))}
                </div>
                <button
                    type="button"
                    aria-label="Close monthly report"
                    className="shrink-0 rounded-full p-2 hover:bg-black/10"
                    onClick={() => navigate("./../../..")}
                >
                    <XMarkIcon className="h-5 w-5" />
                </button>
            </div>
            <div className="flex items-center justify-between gap-2 px-2">
                <p className="text-xs text-stone-600">Policy #{report.layers[0]?.policy.number}</p>
                <button
                    onClick={() => {
                        if (ref.current) {
                            printElement(ref.current);
                        }
                    }}
                    className="flex items-center gap-1 text-left text-sm text-blue-600 small-caps"
                >
                    <PrinterIcon className="h-3 w-3" />
                    Print report
                </button>
            </div>
            {step === "equipment" && (
                <div className="flex min-h-0 grow flex-col p-4">
                    <div className="min-h-0 min-w-0 grow overflow-y-auto overflow-x-hidden">
                        <MonthlyReportUnitsTable data={data} report={report.report} />
                    </div>
                </div>
            )}
            {step === "input" && (
                <div className="grow overflow-auto p-4">
                    <MonthlyReportInputs report={report.report} data={data} />
                </div>
            )}
            {step === "premium" && (
                <div className="grow overflow-auto p-4">
                    <div className={classNames("flex h-full flex-col items-end justify-between gap-4")}>
                        <MonthlyReportCalculations data={data} />
                    </div>
                </div>
            )}
            {step === "submit" && (
                <div className="grow overflow-auto p-4">
                    <MonthlyReportSubmit report={report.report} billingDetails={billingDetails} />
                </div>
            )}
            <PendingPolicyChangesFooter placement="inline" />
            <div className="flex flex-col items-end gap-6 border-t p-4">
                {step === "premium" && (
                    <div>
                        <BillingDetails report={report.report} data={data} billingDetails={billingDetails} />
                    </div>
                )}
                <div className="flex w-full items-center justify-between">
                    <div className="flex flex-1 justify-center">
                        {arrearsReminder?.show && (
                            <div className="flex flex-row items-center gap-2 rounded-md bg-amber-100/50 p-2">
                                <ExclamationTriangleIcon className="h-5 w-5 text-amber-700" />
                                <span className="flex-wrap text-center text-sm text-amber-700">
                                    This report was due on {arrearsReminder?.dueMonth.format("Mmmm")} 10th. Equipment changes for the
                                    previous month can be made via Waypoint until {arrearsReminder?.dueMonth.format("Mmmm")} 14th. <br />
                                    Please call your account manager to make changes.
                                </span>
                            </div>
                        )}
                    </div>
                    {step !== "submit" ? (
                        <Tooltip title={hasPendingPolicyChanges ? "Complete pending policy changes before continuing." : ""}>
                            <span>
                                <PrimaryButton
                                    className=""
                                    disabled={billingDetails.isLoading || billingDetails.isUpdating || hasPendingPolicyChanges}
                                    onClick={() => {
                                        goNext();
                                    }}
                                >
                                    {step === "premium" && !report.report.isFinalized
                                        ? billingDetails.billingSettings?.autoPayEnabled && billingDetails.selectedPaymentMethod
                                            ? "Report & Pay"
                                            : "Report"
                                        : "Continue"}
                                </PrimaryButton>
                            </span>
                        </Tooltip>
                    ) : report.report.isSubmitted || report.report.isFinalized ? (
                        <PrimaryButton disabled className="min-w-[120px] bg-emerald-600">
                            <CheckCircleIcon className="h-4 w-4" />
                            Submitted
                        </PrimaryButton>
                    ) : (
                        <PrimaryButton
                            className="min-w-[120px]"
                            onClick={() => {
                                if (countdown === 0) {
                                    submit.mutate({
                                        policyId: policyId!,
                                        year,
                                        month,
                                    });
                                }
                            }}
                            disabled={countdown > 0 || submit.isPending || submit.isSuccess}
                        >
                            {(submit.isPending || submit.isSuccess) && <CircularProgress size="1rem" classes={{ svg: "text-white" }} />}
                            {countdown || "Submit"}
                        </PrimaryButton>
                    )}
                </div>
            </div>
        </div>
    );
}

function BillingDetails(options: {
    report: MonthlyReport;
    data: MonthlyReportData;
    billingDetails: ReturnType<typeof useBillingDetails>;
}): JSX.Element | null {
    const { billingDetails, report } = options;
    if (!billingDetails.billingSettings || !billingDetails.billingSettings?.autoPayEnabled) return null;

    return (
        <div className="flex w-full min-w-[300px] flex-col gap-4">
            <PaymentMethodSelector billingDetails={billingDetails} report={report} />
            <PaymentDateSelector billingDetails={billingDetails} />
        </div>
    );
}

function PaymentDateSelector({ billingDetails }: { billingDetails: ReturnType<typeof useBillingDetails> }) {
    const options = useMemo(() => {
        return [
            {
                label: "Due date",
                value: billingDetails.targetBillingPeriod ? billingDetails.targetBillingPeriod.dueDate.format("MM/DD/YYYY") : "No due date",
            },
        ];
    }, [billingDetails.targetBillingPeriod]);

    return <TextField label="Auto-payment scheduled for" disabled value={options[0]?.value} variant="outlined" size="small" />;
}

function PaymentMethodSelector({
    report,
    billingDetails,
}: {
    report: MonthlyReport;
    billingDetails: ReturnType<typeof useBillingDetails>;
}): JSX.Element {
    const {
        paymentMethodOptions,
        selectedPaymentMethod,
        isLoading,
        isUpdating,
        onChangePaymentMethod,
        onAddPaymentMethod,
    } = billingDetails;
    const [creatingPaymentMethod, setCreatingPaymentMethod] = useState(false);

    if (isLoading) {
        return <Skeleton variant="text" width={200} height={20} className="mb-2" />;
    }

    return (
        <>
            <Autocomplete<PaymentMethodInfo, false, true, false>
                options={paymentMethodOptions}
                value={selectedPaymentMethod!}
                disabled={isLoading || isUpdating || report.isFinalized}
                getOptionLabel={(option) => option?.description || ""}
                getOptionSelected={(option, value) => option?.id === value.id}
                disableClearable={true}
                onChange={async (event, newValue) => {
                    if (isLoading) return;
                    if (newValue?.id === "other") {
                        setCreatingPaymentMethod(true);
                        return;
                    }
                    await onChangePaymentMethod(newValue);
                    useSnackbar.add(`Policy payment method updated`, { variant: "success" });
                }}
                noOptionsText="No payment methods found"
                renderInput={(params) => {
                    return (
                        <TextField
                            {...params}
                            label="Payment method"
                            value={selectedPaymentMethod?.description}
                            variant="outlined"
                            size="small"
                        />
                    );
                }}
            />
            <Dialog
                open={creatingPaymentMethod && !isLoading}
                onClose={() => {
                    setCreatingPaymentMethod(false);
                }}
                className="overflow-y-auto p-4 pb-0"
            >
                <div className="p-4 pb-0">
                    <NewPaymentMethodForm
                        onSubmit={async (setupIntent) => {
                            if (isLoading) return;
                            await onAddPaymentMethod(setupIntent);
                            setCreatingPaymentMethod(false);
                        }}
                    />
                </div>
            </Dialog>
        </>
    );
}

function MonthlyReportInputs({ report, data }: { report: MonthlyReport; data: MonthlyReportData }) {
    const accountId = useAccountId();
    const mileageRef = useRef<HTMLInputElement>(null);
    const revenueRef = useRef<HTMLInputElement>(null);
    const payrollRefs = useRef<{ [key: string]: HTMLInputElement }>({});

    const { mutate: _update } = api.monthlyReports.useUpdate(accountId!, report.policyId, report.year, report.month, {
        onMutate: (newData) => {
            if (
                queryClient.getQueryData(api.monthlyReports.queryKeys.findForMonth(accountId!, report.policyId, report.year, report.month))
            ) {
                queryClient.setQueryData<MonthlyReportQueryResult>(
                    api.monthlyReports.queryKeys.findForMonth(accountId!, report.policyId, report.year, report.month),
                    (oldData: MonthlyReportQueryResult | undefined) => {
                        if (!oldData) {
                            // this should never happen since we're checking if the querydata exists first
                            return oldData as unknown as MonthlyReportQueryResult;
                        }
                        return {
                            ...oldData,
                            report: { ...oldData.report, ...newData },
                        };
                    }
                );
            }
            if (mileageRef.current && newData.mileage !== undefined) {
                mileageRef.current.value = newData.mileage.toString();
            }
            if (revenueRef.current && newData.revenue !== undefined) {
                revenueRef.current.value = newData.revenue.toString();
            }
            for (const key in payrollRefs.current) {
                const value = newData.payroll?.find((p) => p.description === key)?.amount;
                if (value !== undefined) {
                    payrollRefs.current[key].value = value.toString();
                }
            }
        },
        onError: () => {
            useSnackbar.add("An error occurred while updating your monthly report", { variant: "error" });
            queryClient.invalidateQueries({ queryKey: api.monthlyReports.queryKeys.find(accountId!) });
            if (mileageRef.current) {
                mileageRef.current.value = report.mileage.toString();
            }
            if (revenueRef.current) {
                revenueRef.current.value = report.revenue.toString();
            }
            for (const key in payrollRefs.current) {
                const [coverageOptionId, description] = key.split("_:_");
                const value = report.payroll?.find(
                    (p) => p.description === description && p.coverageOptionId === parseInt(coverageOptionId)
                )?.amount;
                if (value !== undefined) {
                    payrollRefs.current[key].value = value.toString();
                }
            }
        },
    });

    const update = useMemo(
        () =>
            debounce(
                (values: Partial<Pick<MonthlyReport, "mileage" | "revenue" | "payroll">>) => {
                    _update(values);
                },
                500,
                {}
            ),
        [_update]
    );

    return (
        <div className={classNames("flex h-full w-full flex-col items-center justify-center gap-4")}>
            {data.isRevenueReporter && (
                <p className="text-center lg:text-xl">
                    <CurrencyDollarIcon className="-mt-1 mr-2 inline h-6 w-6 text-waypoint-blue lg:h-8 lg:w-8" />
                    {moment()
                        .month(report.month - 1)
                        .format("MMMM")}
                    's revenue was
                    <input
                        placeholder="0"
                        className="mx-2 inline w-36 rounded border px-2 disabled:cursor-not-allowed disabled:bg-stone-100 disabled:text-stone-600"
                        defaultValue={report.revenue}
                        onChange={(e) => {
                            update({
                                revenue: e.target.value ? Math.max(0, parseInt(e.target.value)) : 0,
                            });
                        }}
                        disabled={report.isSubmitted || report.isFinalized}
                        ref={revenueRef}
                        type="number"
                        min={0}
                    />
                    dollars
                </p>
            )}
            {data.isMileageReporter && (
                <p className="text-center lg:text-xl">
                    <MapIcon className="-mt-1 mr-2 inline h-6 w-6 text-waypoint-blue lg:h-8 lg:w-8" />
                    {moment()
                        .month(report.month - 1)
                        .format("MMMM")}
                    's mileage was
                    <input
                        placeholder="0"
                        className="mx-2 inline w-36 rounded border px-2 disabled:cursor-not-allowed disabled:bg-stone-100 disabled:text-stone-600"
                        defaultValue={report.mileage}
                        onChange={(e) => {
                            update({
                                mileage: e.target.value ? Math.max(0, parseInt(e.target.value)) : 0,
                            });
                        }}
                        disabled={report.isSubmitted || report.isFinalized}
                        ref={mileageRef}
                        type="number"
                        min={0}
                    />
                    miles
                </p>
            )}
            {data.isPayrollReporter && (
                <>
                    {Array.from(data.premiumByCoverage).flatMap(([coverageOptionId, { rates }]) =>
                        rates
                            .filter((r) => r.basis === Coverage.Basis.PAYROLL && r.description !== "Terrorism")
                            .map((r) => (
                                <div key={`${coverageOptionId}_:_${r.description}`} className="flex gap-2">
                                    <span>
                                        {moment()
                                            .month(report.month - 1)
                                            .format("MMMM")}
                                        's <span className="font-medium text-waypoint-blue">{r.description}</span> payroll was
                                    </span>
                                    <input
                                        ref={(e) => {
                                            payrollRefs.current[`${coverageOptionId}_:_${r.description}`] = e!;
                                        }}
                                        placeholder="0"
                                        className="w-36 rounded border px-2 disabled:cursor-not-allowed disabled:bg-stone-100 disabled:text-stone-600"
                                        defaultValue={
                                            report.payroll?.find(
                                                (p) => p.description === r.description && p.coverageOptionId === coverageOptionId
                                            )?.amount
                                        }
                                        onChange={(e) => {
                                            update({
                                                payroll: [
                                                    ...report.payroll.filter(
                                                        (p) => !(p.description === r.description && p.coverageOptionId === coverageOptionId)
                                                    ),
                                                    {
                                                        policyId: report.policyId,
                                                        year: report.year,
                                                        month: report.month,
                                                        coverageOptionId,
                                                        description: r.description,
                                                        amount: e.target.value ? Math.max(0, parseInt(e.target.value)) : 0,
                                                    },
                                                ],
                                            });
                                        }}
                                        disabled={report.isSubmitted || report.isFinalized}
                                        type="number"
                                        min={0}
                                    />
                                    <span>dollars</span>
                                </div>
                            ))
                    )}
                </>
            )}
        </div>
    );
}

function MonthlyReportSubmit({ report, billingDetails }: { report: MonthlyReport; billingDetails: ReturnType<typeof useBillingDetails> }) {
    const { submitMessage, success } = useMemo(() => {
        if (billingDetails.billingSettings?.autoPayEnabled && billingDetails.targetBillingPeriod) {
            return {
                submitMessage: `Your report was filed on ${DateTime.today.format(
                    "mm/dd/YYYY"
                )} and payment will be processed on ${billingDetails.targetBillingPeriod.dueDate.format("mm/dd/YYYY")}.`,
                success: true,
            };
        }
        return {
            submitMessage: "Your report was successfully submitted and is awaiting review by your account manager.",
            success: false,
        };
    }, [billingDetails.billingSettings?.autoPayEnabled, billingDetails.targetBillingPeriod]);
    const preSubmitText = useMemo(() => {
        if (
            !report.isSubmitted &&
            !report.isFinalized &&
            billingDetails.billingSettings?.autoPayEnabled &&
            billingDetails.selectedPaymentMethod?.description &&
            billingDetails?.targetBillingPeriod?.dueDate instanceof DateTime
        ) {
            return `By clicking submit you authorize Navigator to process a payment on ${billingDetails?.targetBillingPeriod?.dueDate.format(
                "mm/dd/YYYY"
            )} via ${billingDetails.selectedPaymentMethod?.description}.`;
        }
        if (!report.isSubmitted && !report.isFinalized) {
            return "By submitting this report you are agreeing that the information presented is accurate and truthful to the best of your knowledge.";
        }
        return null;
    }, [
        billingDetails.billingSettings?.autoPayEnabled,
        billingDetails.selectedPaymentMethod?.description,
        billingDetails?.targetBillingPeriod?.dueDate,
        report.isFinalized,
        report.isSubmitted,
    ]);

    return (
        <div className={classNames("flex h-full flex-col items-center justify-center gap-4")}>
            {preSubmitText && (
                <Alert variant="warning" className="max-w-prose [&>p]:text-lg">
                    {preSubmitText}
                </Alert>
            )}
            {report.isFinalized ? (
                <div className="max-w-prose rounded border border-emerald-600 bg-emerald-50 p-4 text-emerald-700">
                    <p className="text-sm">{moment(report.finalizedDate).format("MM/DD/YY")}</p>
                    <p>Your report has been finalized.</p>
                </div>
            ) : (
                report.isSubmitted && (
                    <div
                        className={classNames("max-w-prose rounded border p-4", {
                            "border-blue-600 bg-blue-50 text-blue-700": !success,
                            "border-green-600 bg-green-50 text-green-700": success,
                        })}
                    >
                        <p className="text-sm">{moment(report.submittedDate).format("MM/DD/YY")}</p>
                        <p>{submitMessage}</p>
                    </div>
                )
            )}
        </div>
    );
}
